import { formatLocalDate } from '../utils/localDateTime';
import React, { useState, useEffect } from 'react';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';
import type { Organization, GlobalAsset, EnterpriseAuditResult } from '@types';
import { ICONS } from './constants';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface EnterpriseAuditEngineProps {
    isOpen: boolean;
    onClose: () => void;
    organization: Organization;
    assets?: GlobalAsset[];
    activeThreatsCount?: number;
    onComplete: (result: EnterpriseAuditResult) => void;
}

export const EnterpriseAuditEngine: React.FC<EnterpriseAuditEngineProps> = ({ isOpen, onClose, organization, assets, activeThreatsCount, onComplete }) => {
    const [progress, setProgress] = useState(0);
    const [loadingText, setLoadingText] = useState('Initializing Audit Protocol...');

    useEffect(() => {
        if (!isOpen) {
            setProgress(0);
            return;
        }

        let isMounted = true;

        const runAudit = async () => {
            try {
                // Step 1: Initializing
                if (isMounted) setLoadingText(`Authenticating Organization Profile: ${getOrganizationDisplayName(organization)}`);
                await new Promise(r => setTimeout(r, 1500));
                if (isMounted) setProgress(20);

                // Step 2: Ingest
                if (isMounted) setLoadingText(`Ingesting ${assets?.length || 0} Assets & ${activeThreatsCount || 0} Critical Proximity Threats...`);
                await new Promise(r => setTimeout(r, 2000));
                if (isMounted) setProgress(45);

                // Step 3: AI synthesis
                if (isMounted) setLoadingText('Synthesizing Enterprise Risk Posture against MLCOA & MDCOA projections...');

                const functions = getFunctions();
                const generateEnterpriseRiskAudit = httpsCallable(functions, 'generateEnterpriseRiskAudit');
                const response = await generateEnterpriseRiskAudit({
                    organizationId: organization.id,
                    industry: organization.industry || 'General',
                    assets: assets,
                    threatLevelContext: activeThreatsCount
                });
                const result = response.data as EnterpriseAuditResult;

                if (isMounted) {
                    setProgress(85);
                    setLoadingText('Persisting Audit to Intelligence Archive...');

                    // --- P3: Persist on-demand audit result to generated_reports ---
                    // Non-blocking: a persistence failure must never prevent the user seeing their report.
                    try {
                        const { db, auth } = await import('../../firebaseConfig');
                        const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
                        const { sanitizeFirestoreData } = await import('./reports/ReportShared');

                        if (db && auth?.currentUser) {
                            await addDoc(collection(db, 'generated_reports'), {
                                reportType:     'EnterpriseRiskAudit',
                                type:           'enterprise_audit',
                                workflowName:   'Enterprise Risk Audit',
                                suiteName:      'Enterprise Command Center',
                                title:          `Enterprise Risk Audit — ${getOrganizationDisplayName(organization)} — ${formatLocalDate(new Date())}`,
                                organizationId: organization.id,
                                resultData:     sanitizeFirestoreData(result),
                                timestamp:      serverTimestamp(),
                                userEmail:      auth.currentUser.email,
                                userName:       auth.currentUser.displayName || auth.currentUser.email,
                            });
                            
                            console.log('[EnterpriseAuditEngine] Audit result persisted to generated_reports.');
                        }
                    } catch (persistErr) {
                        console.warn('[EnterpriseAuditEngine] Could not persist audit to Firestore:', persistErr);
                    }
                    // --- END P3 ---

                    await new Promise(r => setTimeout(r, 1500));
                    setProgress(100);
                    setTimeout(() => {
                        onComplete(result);
                    }, 800);
                }
            } catch (error) {
                console.error("Enterprise Audit Error", error);
                if (isMounted) setLoadingText('ERROR: Risk Audit Failed. Check console logs.');
            }
        };

        runAudit();

        return () => { isMounted = false; };
    }, [isOpen, organization, assets, activeThreatsCount, onComplete]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center animate-fade-in p-4">
            <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-xl" />
            
            <div className="relative w-full max-w-3xl glass-panel p-10 flex flex-col items-center justify-center transform transition-transform animate-scale-in text-center shadow-[0_0_100px_rgba(239,68,68,0.2)] border border-red-500/30 rounded-2xl">
                
                <div className="mb-8 relative">
                    <div className="absolute inset-0 bg-red-500/20 blur-[50px] rounded-full animate-pulse" />
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-32 h-32 text-red-500 relative z-10 animate-[spin_4s_linear_infinite]">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center z-20 text-white">
                        {ICONS.activity}
                    </div>
                </div>

                <h2 className="text-3xl font-black text-white tracking-widest uppercase mb-4 shadow-black drop-shadow-lg">
                    Enterprise Risk Audit
                </h2>

                <p className="text-red-400 font-mono text-sm tracking-widest mb-10 h-6">
                    {loadingText}
                </p>

                <div className="w-full bg-slate-800 rounded-full h-2 mb-4 overflow-hidden border border-slate-700">
                    <div 
                        className="bg-gradient-to-r from-red-600 to-orange-400 h-2 rounded-full transition-all duration-500 ease-out relative" 
                        style={{ width: `${progress}%` }}
                    >
                        <div className="absolute top-0 right-0 bottom-0 w-20 bg-white/30 blur-[4px]" />
                    </div>
                </div>

                <div className="flex justify-between w-full text-slate-500 font-mono text-xs uppercase tracking-widest">
                    <span>{progress}% Completed</span>
                    <span>System Online</span>
                </div>
            </div>
        </div>
    );
};
